import React from 'react';
import { Calendar, CheckCircle2, Clock, Zap } from 'lucide-react';

const PhasesSection = () => {
  const phases = [
    {
      phase: 1,
      duration: "0-30 dias",
      title: "Implantação Inicial",
      color: "from-[#007bff] to-[#0056b3]",
      icon: Zap,
      items: [
        "Gestão completa de passageiros e credenciais",
        "App do motorista com validação NFC/Bluetooth",
        "Controle de acesso e autorizações",
        "Operação assistida e treinamento completo",
        "Suporte dedicado para estabilização"
      ],
      goal: "Estabilizar a operação e ganhar confiança",
      bgColor: "bg-[#007bff]"
    },
    {
      phase: 2,
      duration: "Após 30 dias",
      title: "Expansão de Recursos",
      color: "from-[#008080] to-[#006666]",
      icon: CheckCircle2,
      items: [
        "App do condômino completo e funcional",
        "Credencial virtual integrada ao celular",
        "Georreferenciamento em tempo real",
        "Visualização de veículos no mapa",
        "Relatórios avançados e analytics"
      ],
      goal: "Ampliar transparência e valor percebido",
      bgColor: "bg-[#008080]"
    }
  ];

  return (
    <section id="phases" className="py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#007bff]/10 px-4 py-2 rounded-full border border-[#007bff]/20 mb-6">
            <Calendar className="w-4 h-4 text-[#007bff]" />
            <span className="text-sm font-medium text-[#007bff]">Cronograma</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Implantação em 2 Fases
          </h2>
          <p className="text-xl text-gray-600">
            Abordagem gradual para reduzir riscos e maximizar resultados
          </p>
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {phases.map((phase, index) => {
              const Icon = phase.icon;
              return (
                <div 
                  key={index}
                  className="group relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:-translate-y-1"
                >
                  {/* Header with gradient */}
                  <div className={`bg-gradient-to-r ${phase.color} p-8 relative overflow-hidden`}>
                    <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
                    
                    <div className="relative">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <Icon className="w-7 h-7 text-white" />
                          </div>
                          <div>
                            <p className="text-white/80 text-sm font-medium">Fase {phase.phase}</p>
                            <p className="text-white text-2xl font-bold">{phase.duration}</p>
                          </div>
                        </div>
                        
                        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <span className="text-3xl font-bold text-white">{phase.phase}</span>
                        </div>
                      </div>
                      
                      <h3 className="text-3xl font-bold text-white">
                        {phase.title}
                      </h3>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8">
                    <div className="space-y-4 mb-8">
                      {phase.items.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3 group/item">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#007bff]/10 flex items-center justify-center mt-0.5 group-hover/item:bg-[#007bff] transition-colors duration-300">
                            <CheckCircle2 className="w-4 h-4 text-[#007bff] group-hover/item:text-white transition-colors duration-300" />
                          </div>
                          <p className="text-gray-700 leading-relaxed">
                            {item}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Goal badge */}
                    <div className={`${phase.bgColor}/10 rounded-2xl p-6 border border-${phase.bgColor}/20`}>
                      <div className="flex items-start gap-3">
                        <Zap className={`w-5 h-5 ${phase.bgColor.replace('bg-', 'text-')} flex-shrink-0 mt-1`} />
                        <div>
                          <p className="text-sm font-semibold text-gray-900 mb-1">Objetivo da Fase:</p>
                          <p className="text-gray-700 font-medium">{phase.goal}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Timeline connector for desktop */}
          <div className="hidden lg:block relative -mt-4 mb-12">
            <div className="flex items-center justify-center gap-4">
              <div className="h-1 w-32 bg-gradient-to-r from-[#007bff] to-[#008080]"></div>
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#007bff] to-[#008080] flex items-center justify-center shadow-lg">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div className="h-1 w-32 bg-gradient-to-r from-[#008080] to-[#007bff]"></div>
            </div>
          </div>

          {/* Bottom highlight */}
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-[#007bff]/5 to-[#008080]/5 rounded-3xl p-10 border border-[#007bff]/20">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#007bff] to-[#008080] flex items-center justify-center shadow-lg">
                  <CheckCircle2 className="w-10 h-10 text-white" />
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Implantação Gradual e Segura
                </h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  A abordagem faseada garante <span className="font-bold text-[#007bff]">estabilidade operacional</span> desde 
                  o início, permitindo ajustes antes da expansão completa. Reduzimos riscos e maximizamos a 
                  <span className="font-bold text-[#008080]"> adoção por usuários e gestores</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhasesSection;
